<template>
  <div class="wizard-overlay">
    <div class="wizard-modal">
      <div class="wizard-header">
        <button class="wizard-close" @click="closeWizard">✕</button>
        <h2 class="title">
          <lang labelKey="HEADLINE-WIZARD-SETUP" />
        </h2>
        <h3 class="pharmacy-name subtitle">
          {{ pageTitle }}
        </h3>
      </div>

      <div class="wizard-steps-container">
        <!-- PRE-WIZARD NOTIFICATION -->
        <div v-if="preWizard" class="pre-wizard-info">
          <h4 class="h4-title">
            Bitte stellen Sie vor dem Start sicher, dass folgende Informationen
            vorhanden sind:
          </h4>
          <ul class="pre-wizard-list">
            <li>
              <span class="material-symbols-outlined">mail</span> Email muss
              vorhanden sein
            </li>
            <li>
              <span class="material-symbols-outlined">cloud_upload</span> FTP
              muss eingerichtet sein
            </li>
            <li>
              <span class="material-symbols-outlined">vpn_key</span> FTP Daten
              müssen vorhanden sein
            </li>
            <li>
              <span class="material-symbols-outlined">palette</span> Farbwerte
              müssen bekannt sein
            </li>
          </ul>
          <div class="pre-wizard-actions">
            <button class="button wizard-button btn-back" disabled>
              <lang labelKey="WIZARD-SETUP-BACK-BTN" />
            </button>
            <button
              class="button wizard-button btn-next"
              @click="preWizard = false"
            >
              <lang labelKey="WIZARD-SETUP-NEXT-BTN" />
            </button>
          </div>
        </div>

        <!-- EXISTING WIZARD STEPS -->
        <div v-else>
          <div v-if="validationError" class="intermediate-step error-state">
            <div class="status-icon icon-error">
              <span style="font-size: 90px" class="material-symbols-outlined"
                >error</span
              >
            </div>
            <h4 class="h4-title">
              <lang labelKey="WIZARD-SETUP-ERROR_MESSAGE" />
            </h4>
            <p class="status-message">{{ validationError }}</p>
            <ul v-if="validationActions.length" class="validation-actions">
              <li
                v-for="(action, index) in validationActions"
                :key="index"
                :class="{ success: action.success, error: !action.success }"
              >
                {{ action.name }}
                <span class="action-status">
                  <span
                    v-if="action.success"
                    class="material-symbols-outlined status-icon-success"
                  >
                    check_circle
                  </span>
                  <span
                    v-else
                    class="material-symbols-outlined status-icon-error"
                    :title="action.error"
                  >
                    cancel
                  </span>
                </span>
              </li>
            </ul>
            <div class="wizard-actions">
              <button class="button btn-back" @click="resetValidation">
                <lang labelKey="WIZARD-SETUP-BACK-BTN" />
              </button>
            </div>
          </div>

          <div
            v-else-if="validationSuccess"
            class="intermediate-step success-state"
          >
            <div class="status-icon icon-success">
              <span style="font-size: 90px" class="material-symbols-outlined"
                >check_circle</span
              >
            </div>
            <h4 class="h4-title">
              <lang labelKey="WIZARD-SETUP-SUCCESS_MESSAGE" />
            </h4>
            <ul v-if="validationActions.length" class="validation-actions">
              <li
                v-for="(action, index) in validationActions"
                :key="index"
                :class="{ success: action.success, error: !action.success }"
              >
                {{ action.name }}
                <span class="action-status">
                  <span
                    v-if="action.success"
                    class="material-symbols-outlined status-icon-success"
                  >
                    check_circle
                  </span>
                  <span
                    v-else
                    class="material-symbols-outlined status-icon-error"
                    :title="action.error"
                  >
                    cancel
                  </span>
                </span>
              </li>
            </ul>
            <div class="wizard-actions">
              <button class="button btn-next" @click="confirmSuccess">
                <lang
                  :labelKey="
                    step === 4
                      ? 'WIZARD-SETUP-CLOSE-LAST-STEP'
                      : 'WIZARD-SETUP-NEXT-STEP'
                  "
                />
              </button>
            </div>
          </div>
          <div v-else class="wizard-form-content">
            <steps
              :current="step - 1"
              type="line"
              prev-text="Zurück"
              next-text="Weiter"
            >
              <div v-if="step === 1">
                <h4 class="h4-title">
                  <lang labelKey="WIZARD-SETUP-STEP-1-TITLE" />
                </h4>
                <div class="control">
                  <div class="apopoint-options">
                    <div
                      v-for="type in apopointTypes"
                      :key="type.value"
                      :class="[
                        'apopoint-option',
                        { selected: wizardData.apopointType === type.value },
                      ]"
                      @click="wizardData.apopointType = type.value"
                    >
                      {{ type.label }}
                    </div>
                  </div>
                </div>
              </div>

              <div v-if="step === 2">
                <h4 class="h4-title">
                  <lang labelKey="WIZARD-SETUP-STEP-2-TITLE" />
                </h4>
                <div class="control">
                  <label
                    ><lang labelKey="WIZARD-SETUP-STEP-2-SERVER_LABEL"
                  /></label>
                  <div class="server-options">
                    <div
                      v-for="server in servers"
                      :key="server.value"
                      :class="[
                        'server-option',
                        { selected: wizardData.server === server.value },
                      ]"
                      @click="wizardData.server = server.value"
                    >
                      {{ server.label }}
                    </div>
                  </div>
                </div>
                <div class="control">
                  <label
                    ><lang labelKey="WIZARD-SETUP-STEP-3-URL_LABEL"
                  /></label>
                  <input
                    class="input"
                    v-model="wizardData.url"
                    placeholder="https://..."
                  />
                </div>
                <div class="control">
                  <label
                    ><lang labelKey="WIZARD-SETUP-STEP-2-FTP_USERNAME_LABEL"
                  /></label>
                  <input
                    class="input"
                    v-model="wizardData.ftpUsername"
                    placeholder="z.B. meine-apotheke"
                  />
                </div>
                <div class="control">
                  <label
                    ><lang labelKey="WIZARD-SETUP-STEP-2-FTP_PASSWORD_LABEL"
                  /></label>
                  <input
                    class="input"
                    type="password"
                    v-model="wizardData.ftpPassword"
                    placeholder="••••••"
                  />
                </div>
              </div>

              <div v-if="step === 3">
                <h4 class="h4-title">
                  <lang labelKey="WIZARD-SETUP-STEP-3-TITLE" />
                </h4>
                <div class="control">
                  <label
                    ><lang labelKey="WIZARD-SETUP-STEP-3-NAME_LABEL"
                  /></label>
                  <input
                    class="input"
                    v-model="wizardData.name"
                    placeholder="Meine Lieblingsapotheke"
                  />
                </div>
                <div class="control">
                  <label
                    ><lang labelKey="WIZARD-SETUP-STEP-3-SENDER_EMAIL_LABEL"
                  /></label>
                  <input
                    class="input"
                    type="email"
                    v-model="wizardData.senderEmail"
                    placeholder="meine-apotheke@apopoint.at"
                  />
                </div>
                <div class="control has-content-feed">
                  <label>
                    <input
                      type="checkbox"
                      v-model="wizardData.hasContentFeed"
                      class="checkbox-input"
                    />

                    <span class="checkbox-label">
                      <lang
                        labelKey="WIZARD-SETUP-STEP-3-HASCONTENTFEED_LABEL"
                      />
                    </span>
                  </label>
                </div>
              </div>

              <div v-if="step === 4">
                <h4 class="h4-title">
                  <lang labelKey="WIZARD-SETUP-STEP-4-TITLE" />
                </h4>
                <div class="branding-files">
                  <!-- LOGO UPLOAD -->
                  <div class="file-upload">
                    <label class="label-step-4">
                      <lang labelKey="WIZARD-SETUP-STEP-4-LOGO_LABEL" />
                    </label>
                    <p class="file-info-text">
                      <lang labelKey="WIZARD-SETUP-HELP-INFO_LOGO" />
                    </p>
                    <div class="file-box">
                      <input
                        type="file"
                        accept="image/*"
                        @change="onImageUpload($event, 'logo')"
                      />
                      <!-- PREVIEW -->
                      <img
                        v-if="wizardData.branding.logo"
                        :src="wizardData.branding.logo"
                        class="image-preview"
                        alt="Logo preview"
                      />
                      <!-- PLACEHOLDER -->
                      <span v-else>Datei auswählen</span>
                    </div>
                  </div>
                  <!-- FAVICON UPLOAD -->
                  <div class="file-upload">
                    <label class="label-step-4">
                      <lang labelKey="WIZARD-SETUP-STEP-4-FAVICON_LABEL" />
                    </label>
                    <p class="file-info-text">
                      <lang labelKey="WIZARD-SETUP-HELP-INFO_FAVICON" />
                    </p>
                    <div class="file-box">
                      <input
                        type="file"
                        accept="image/*"
                        @change="onImageUpload($event, 'favicon')"
                      />
                      <!-- PREVIEW -->
                      <img
                        v-if="wizardData.branding.favicon"
                        :src="wizardData.branding.favicon"
                        class="image-preview"
                        alt="Favicon preview"
                      />
                      <!-- PLACEHOLDER -->
                      <span v-else>Datei auswählen</span>
                    </div>
                  </div>
                </div>
                <div class="branding-colors">
                  <div class="color-item">
                    <label class="label-step-4"
                      ><lang labelKey="BRANDING_COLORS_PRIMARY"
                    /></label>
                    <div
                      class="color-preview"
                      :style="{
                        backgroundColor: isValidHex(
                          wizardData.branding.colors.primary
                        )
                          ? wizardData.branding.colors.primary
                          : '#ffffff',
                        color: '#ffffff',
                      }"
                    >
                      WCAG Test
                    </div>
                    <input
                      type="text"
                      v-model="wizardData.branding.colors.primary"
                      placeholder="#RRGGBB"
                    />
                    <div class="wcag-indicator">
                      <template
                        v-if="isValidHex(wizardData.branding.colors.primary)"
                      >
                        <span
                          class="wcag-icon"
                          :class="
                            primaryWcagValid ? 'wcag-valid' : 'wcag-invalid'
                          "
                        >
                          <span
                            class="material-symbols-outlined"
                            :class="
                              primaryWcagValid
                                ? 'wcag-valid-text'
                                : 'wcag-invalid-text'
                            "
                          >
                            {{ primaryWcagValid ? "check" : "close" }}
                          </span>
                        </span>

                        <span class="wcag-text">
                          {{
                            primaryWcagValid
                              ? "WCAG konform"
                              : "Nicht WCAG konform"
                          }}
                        </span>
                      </template>

                      <!-- Ako ništa nije uneseno ili nije validan HEX -->
                      <template v-else>
                        <span class="wcag-hint"> HEX code mit # eingeben </span>
                      </template>
                    </div>
                  </div>

                  <div class="color-item">
                    <label class="label-step-4"
                      ><lang labelKey="BRANDING_COLORS_SECONDARY"
                    /></label>
                    <div
                      class="color-preview"
                      :style="{
                        backgroundColor: isValidHex(
                          wizardData.branding.colors.secondary
                        )
                          ? wizardData.branding.colors.secondary
                          : '#ffffff',
                        color: '#ffffff',
                      }"
                    >
                      WCAG Test
                    </div>
                    <input
                      type="text"
                      v-model="wizardData.branding.colors.secondary"
                      placeholder="#RRGGBB"
                    />
                    <div class="wcag-indicator">
                      <template
                        v-if="isValidHex(wizardData.branding.colors.secondary)"
                      >
                        <span
                          class="wcag-icon"
                          :class="
                            secondaryWcagValid ? 'wcag-valid' : 'wcag-invalid'
                          "
                        >
                          <span class="material-symbols-outlined">
                            {{ secondaryWcagValid ? "check" : "close" }}
                          </span>
                        </span>

                        <span class="wcag-text">
                          {{
                            secondaryWcagValid
                              ? "WCAG konform"
                              : "Nicht WCAG konform"
                          }}
                        </span>
                      </template>

                      <!-- Ako ništa nije uneseno ili nije validan HEX -->
                      <template v-else>
                        <span class="wcag-hint"> HEX code mit # eingeben </span>
                      </template>
                    </div>
                  </div>

                  <div
                    class="color-item"
                    v-if="wizardData.apopointType !== 'apopoint-starter'"
                  >
                    <label class="label-step-4">
                      <lang labelKey="BRANDING_COLORS_ACCENT" />
                    </label>
                    <div
                      class="color-preview"
                      :style="{
                        backgroundColor: isValidHex(
                          wizardData.branding.colors.accent
                        )
                          ? wizardData.branding.colors.accent
                          : '#ffffff',
                        color: '#212121',
                      }"
                    >
                      WCAG Test
                    </div>
                    <input
                      type="text"
                      v-model="wizardData.branding.colors.accent"
                      placeholder="#RRGGBB"
                    />
                    <div class="wcag-indicator">
                      <template
                        v-if="isValidHex(wizardData.branding.colors.accent)"
                      >
                        <span
                          class="wcag-icon"
                          :class="
                            accentWcagValid ? 'wcag-valid' : 'wcag-invalid'
                          "
                        >
                          <span class="material-symbols-outlined">
                            {{ accentWcagValid ? "check" : "close" }}
                          </span>
                        </span>
                        <span class="wcag-text">
                          {{
                            accentWcagValid
                              ? "WCAG konform"
                              : "Nicht WCAG konform"
                          }}
                        </span>
                      </template>
                      <template v-else>
                        <span class="wcag-hint"> HEX code mit # eingeben </span>
                      </template>
                    </div>
                  </div>
                </div>
              </div>

              <step title="Apopoint-Typ" description=" "></step>
              <step title="Server & FTP" description=" "></step>
              <step title="Grundinformationen" description=" "></step>
              <step title="Branding" description=" "></step>
            </steps>

            <div class="wizard-actions">
              <button
                class="button wizard-button btn-back"
                @click="prevStep"
                :disabled="step === 1 || isValidating"
              >
                <lang labelKey="WIZARD-SETUP-BACK-BTN" />
              </button>
              <button
                class="button wizard-button btn-next"
                @click="nextStep"
                :disabled="!canGoNext || isValidating"
              >
                <span v-if="isValidating" class="loading-content">
                  <span class="spinner"></span>
                  Wird geprüft…
                </span>
                <span v-else>
                  <lang
                    :labelKey="
                      step === 4
                        ? 'WIZARD-SETUP-LAST-STEP-BTN'
                        : 'WIZARD-SETUP-NEXT-BTN'
                    "
                  />
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>



<script>
import { mapActions } from "vuex";

export default {
  name: "SetupWizard",
  props: {
    siteId: { type: [String, Number], required: true },
    pageTitle: { type: String, required: true },
  },
  data: function () {
    return {
      preWizard: true,
      step: 1,
      isValidating: false,
      validationSuccess: false,
      validationError: null,
      validationActions: [],
      servers: [
        { value: "1", label: "Server 1" },
        { value: "2", label: "Server 2" },
        { value: "3", label: "Server 3" },
        { value: "4", label: "Server 4" },
        { value: "beta", label: "Beta" },
      ],
      wizardData: {
        apopointType: "apopoint-starter",
        server: "",
        ftpUsername: "",
        ftpPassword: "",
        url: "",
        name: "",
        senderEmail: "",
        hasContentFeed: true, // <-- default true
        branding: {
          logo: "",
          favicon: "",
          colors: { primary: "", secondary: "", accent: "" },
        },
      },

      apopointTypes: [
        { value: "apopoint-starter", label: "Apopoint Starter" },
        { value: "apopoint-shop", label: "Apopoint Shop" },
      ],
    };
  },
  mounted() {
    document.body.style.overflow = "hidden";
  },
  beforeUnmount() {
    document.body.style.overflow = "";
  },
  computed: {
    primaryWcagValid() {
      return this.isWcagCompliant(this.wizardData.branding.colors.primary);
    },
    secondaryWcagValid() {
      return this.isWcagCompliant(this.wizardData.branding.colors.secondary);
    },
    accentWcagValid() {
      return this.isWcagCompliant(
        this.wizardData.branding.colors.accent,
        "#212121"
      );
    },
    stepTitles() {
      return {
        apopoint: this.$lang("STEP-APOPOINT"),
        server: this.$lang("STEP-SERVER"),
        basic: this.$lang("STEP-BASIC"),
        branding: this.$lang("STEP-BRANDING"),
      };
    },
    isStep2Valid: function () {
      return (
        this.wizardData.server &&
        this.wizardData.ftpUsername &&
        this.wizardData.ftpUsername.length >= 3 &&
        this.wizardData.ftpPassword &&
        this.wizardData.ftpPassword.length >= 3
      );
    },
    isStep3Valid: function () {
      return (
        this.wizardData.url &&
        this.wizardData.name &&
        this.isValidEmail(this.wizardData.senderEmail)
      );
    },
    isStep4Valid() {
      const c = this.wizardData.branding.colors;
      const accentValid =
        this.wizardData.apopointType === "apopoint-starter"
          ? true
          : this.isValidHex(c.accent);

      return (
        this.wizardData.branding.logo &&
        this.wizardData.branding.favicon &&
        this.isValidHex(c.primary) &&
        this.isValidHex(c.secondary) &&
        accentValid
      );
    },

    canGoNext: function () {
      if (this.step === 1) return true;
      if (this.step === 2) return this.isStep2Valid;
      if (this.step === 3) return this.isStep3Valid;
      if (this.step === 4) return this.isStep4Valid;
      return false;
    },
  },
  methods: {
    isValidHex(color) {
      return /^#[0-9A-Fa-f]{6}$/.test(color);
    },

    hexToRgb(hex) {
      hex = hex.replace("#", "");
      if (hex.length === 3) {
        hex = hex
          .split("")
          .map((c) => c + c)
          .join("");
      }
      const bigint = parseInt(hex, 16);
      return {
        r: (bigint >> 16) & 255,
        g: (bigint >> 8) & 255,
        b: bigint & 255,
      };
    },
    luminance(r, g, b) {
      const a = [r, g, b].map((v) => {
        v = v / 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
      });
      return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
    },
    getContrast(hex1, hex2) {
      const rgb1 = this.hexToRgb(hex1);
      const rgb2 = this.hexToRgb(hex2);
      const L1 = this.luminance(rgb1.r, rgb1.g, rgb1.b);
      const L2 = this.luminance(rgb2.r, rgb2.g, rgb2.b);
      return L1 > L2 ? (L1 + 0.05) / (L2 + 0.05) : (L2 + 0.05) / (L1 + 0.05);
    },
    isWcagCompliant(colorHex, textColorHex = "#ffffff") {
      if (!this.isValidHex(colorHex)) return null;
      const contrast = this.getContrast(colorHex, textColorHex);
      return contrast >= 4.5;
    },

    closeWizard() {
      this.$emit("close");
      document.body.style.overflow = "";
    },
    ...mapActions(["validateStep"]),
    isValidEmail: function (email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    },
    onImageUpload: function (event, type) {
      var _this = this;
      var file = event.target.files[0];
      if (!file) return;
      var reader = new FileReader();
      reader.onload = function () {
        _this.wizardData.branding[type] = reader.result;
      };
      reader.readAsDataURL(file);
    },
    resetValidation: function () {
      this.validationError = null;
    },
    confirmSuccess() {
      this.validationSuccess = false;

      if (this.step < 4) {
        this.step++;
      } else {
        this.$emit("wizard-completed", this.wizardData);
        this.closeWizard();
      }
    },

    nextStep: async function () {
      if (this.step === 1) {
        this.step++;
        return;
      }

      this.isValidating = true;
      this.validationError = null;

      try {
        var payload = null;
        var res = null;

        if (this.step === 2) {
          payload = {
            siteId: this.siteId,
            url: this.wizardData.url,
            shopType:
              this.wizardData.apopointType === "apopoint-shop"
                ? "APOPOINT_PWA"
                : "WEBSITE_PWA",
            server: this.wizardData.server,
            username: this.wizardData.ftpUsername,
            password: btoa(this.wizardData.ftpPassword),
          };
          res = await this.validateStep({ step: 1, payload: payload });
        } else if (this.step === 3) {
          payload = {
            siteId: this.siteId,
            name: this.wizardData.name,
            senderEmail: this.wizardData.senderEmail,
            hasContentFeed: this.wizardData.hasContentFeed, // <-- include hasContentFeed in payload
          };
          res = await this.validateStep({ step: 2, payload: payload });
        } else if (this.step === 4) {
          const colorsToSend = { ...this.wizardData.branding.colors };

          if (this.wizardData.apopointType === "apopoint-starter") {
            colorsToSend.accent = "#E4E1D6";
          }

          payload = {
            siteId: this.siteId,
            logo: this.wizardData.branding.logo,
            favicon: this.wizardData.branding.favicon,
            colors: colorsToSend,
          };

          res = await this.validateStep({ step: 3, payload: payload });
        }

        if (res && res.success) {
          this.validationSuccess = true;
          this.validationActions = res.actions || [];
        } else {
          this.validationError =
            res && res.error ? res.error : "Ein Fehler ist aufgetreten.";
          this.validationActions = res.actions || [];
        }
      } catch (err) {
        this.validationError = "API Error: Connection failed.";
      } finally {
        this.isValidating = false;
      }
    },
    prevStep: function () {
      if (this.step > 1) this.step--;
    },
  },
};
</script>

<style scoped>
.wizard-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}
.wizard-modal {
  background: #dee4e9;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  position: relative;
}
.wizard-header {
  background-color: #ffffff;
  padding: 20px 40px;
  margin-bottom: 20px;
}
.wizard-close {
  position: absolute;
  top: 37px;
  right: 20px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
}
.wizard-steps-container {
  margin-top: 50px;
  margin-bottom: 50px;
  width: 950px;
  justify-self: center;
  background-color: #ffffff;
  padding: 50px 40px;
  border-radius: 3px;
  min-height: 500px;
  display: flex;
  flex-direction: column;
}
.wizard-form-content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  flex-grow: 1;
}

/* INTERMEDIATE STATES (Success/Error) */
.intermediate-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-grow: 1;
  text-align: center;
}
.status-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  margin-bottom: 20px;
  color: white;
}
.icon-success {
  background-color: #27ae60;
}
.icon-error {
  background-color: #e74c3c;
}
.status-message {
  font-size: 15px;
  margin-bottom: 30px;
  color: #555;
  max-width: 400px;
}

.wizard-actions {
  margin-top: 40px;
  display: flex;
  justify-content: center;
  gap: 12px;
}

.btn-back {
  background: #f5f5f5;
  color: #555;
  border: 1px solid #ddd;
  max-width: 350px;
  font-size: 15px;
  padding: 16px 25px;
}
.btn-next {
  background: #45a7d6;
  color: #fff;
  max-width: 350px;
  font-size: 15px;
  padding: 16px 25px;
}
.btn-next:disabled {
  background: #aab;
  cursor: not-allowed;
}

.wizard-button {
  font-size: 15px;
  padding: 16px 25px;
}

.control {
  margin-bottom: 20px;
}
.input {
  width: 100%;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.h4-title {
  font-size: 1.5rem;
  margin-bottom: 12px;
}
.step-description {
  margin-bottom: 30px;
  color: #666;
}
.apopoint-options,
.server-options {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 8px;
}
.apopoint-option {
  margin-bottom: 135px;
}
.apopoint-option,
.server-option {
  padding: 12px 20px;
  border: 1px solid #ccc;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
  text-align: center;
}
.selected {
  border-color: #45a7d6;
  background: #e6f0ff;
  font-weight: bold;
}
.branding-files {
  display: flex;
  gap: 24px;
  margin-bottom: 32px;
}
.file-upload {
  flex: 1;
}
.file-box {
  position: relative;
  border: 1px dashed #bbb;
  padding: 16px;
  text-align: center;
  background: #fafafa;
}
.file-box input {
  position: absolute;
  inset: 0;
  opacity: 0;
  cursor: pointer;
}
.file-box {
  position: relative;
  border: 1px dashed #bbb;
  padding: 16px;
  text-align: center;
  background: #fafafa;
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-preview {
  max-width: 100%;
  max-height: 120px;
  object-fit: contain;
  border-radius: 6px;
}

.branding-colors {
  display: flex;
  gap: 24px;
}
.color-item {
  flex: 1;
}
.color-preview {
  height: 42px;
  border-radius: 6px;
  border: 1px solid #ccc;
  margin-bottom: 6px;
  padding-top: 3px;
  padding-left: 5px;
  font-style: italic;
}
.validation-hint {
  color: #e74c3c;
  font-size: 14px;
  margin-bottom: 10px;
}
.loading-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.validation-actions {
  list-style: none;
  padding: 0;
  margin-top: 24px;
  text-align: left;
  max-width: 600px;
  width: 100%;
  border-top: 1px solid #ccc;
  padding-top: 16px;
}

.validation-actions li {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 8px;
  border-radius: 6px;
  font-size: 15px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.validation-actions li.success {
  background-color: #e6f9ed;
  color: #276749;
}

.validation-actions li.error {
  background-color: #fde2e1;
  color: #9e2a2b;
}

.validation-actions .action-name {
  font-weight: 500;
}

.validation-actions .action-status {
  font-weight: bold;
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}
.status-icon-success {
  color: #27ae60;
  font-size: 20px;
  padding-top: 5px;
}

.status-icon-error {
  color: #e74c3c;
  font-size: 20px;
  padding-top: 5px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.file-info-text {
  font-style: italic;
  font-size: 13px;
  color: #7a7979;
  min-height: 40px;
}
.control.has-content-feed {
  display: flex;
  align-items: center;
  margin-top: 8px;
}

.checkbox-input {
  width: 20px;
  height: 20px;
  margin-right: 12px;
  cursor: pointer;
  accent-color: #45a7d6;
  vertical-align: middle;
  margin-top: 0;
  margin-left: 8px;
}

.checkbox-label {
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  user-select: none;
}
</style>
<style>
.step-footer {
  display: none !important;
}
.step-header .step-item i {
  line-height: 20px;
}
div.line .step-item.is-active .step-icon {
  background-color: #45a7d6 !important;
}
.line .step-item.is-done .step-icon {
  background-color: #45a7d6 !important;
}
.line .step-item:after {
  background-color: #45a7d6 !important;
}

.pre-wizard-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  flex-grow: 1;
  text-align: left;
}
.pre-wizard-list {
  list-style: none;
  padding: 0;
  margin: 32px 0 24px 0;
  font-size: 1rem;
  text-align: left;
  width: 100%;
  margin-bottom: 80px;
}
.pre-wizard-list li {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
  font-size: 1rem;
}
.pre-wizard-actions {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 32px;
  gap: 12px;
}
.wcag-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  margin-top: 4px;
  font-weight: 500;
}
.label-step-4 {
  font-size: 1.1rem;
  font-weight: 500;
}

.wcag-icon.wcag-valid {
  background-color: #497e5b;
}
.wcag-valid-text {
  color: #497e5b;
}
.wcag-invalid-text {
  color: #b7312c;
}

.wcag-icon.wcag-invalid {
  background-color: #b7312c;
}

.wcag-text {
  line-height: 1;
}
.wcag-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-top: 6px;
  font-weight: 500;
}

.wcag-icon {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
}

.wcag-icon .material-symbols-outlined {
  font-size: 16px;
  color: #ffffff;
}

.wcag-valid .wcag-icon {
  background-color: #27ae60;
}
.wcag-hint {
  font-size: 13px;
  color: #888;
  font-style: italic;
}

.wcag-invalid .wcag-icon {
  background-color: #e74c3c;
}

.wcag-text {
  line-height: 1;
}
</style>